import React, { Suspense, lazy, useEffect, useState } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import NavBarComponent from "./components/Navbar/NavBarComponent";
import SocialBanner from "./components/SocialBanner/SocialBanner";
import ContactForm from "./components/Home/ContactUs";

// Loading spinner component
const LoadingSpinner = () => (
  <div
    style={{
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      minHeight: "60vh",
      marginTop: "120px",
    }}
  >
    <div
      style={{
        width: "50px",
        height: "50px",
        border: "4px solid #f3f3f3",
        borderTop: "4px solid #007bff",
        borderRadius: "50%",
        animation: "spin 1s linear infinite",
      }}
    />
    <style>{`
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    `}</style>
  </div>
);

// Lazy load all page components for code splitting
const HomePage = lazy(() => import("./components/Home/HomePage"));
const AboutPage = lazy(() => import("./components/About/AboutPage"));
const ShowCase = lazy(() => import("./components/Showcase/ShowCase"));
const ContactPage = lazy(() => import("./components/Contact/ContactPage"));
const Services = lazy(() => import("./components/Our Services/Services"));
const BlogPage = lazy(() => import("./components/Blog/BlogPage"));
const Blog1 = lazy(() => import("./components/Blog/Blog1"));
const Blog2 = lazy(() => import("./components/Blog/Blog2"));
const Blog3 = lazy(() => import("./components/Blog/Blog3"));
const Blog4 = lazy(() => import("./components/Blog/Blog4"));
const Blog5 = lazy(() => import("./components/Blog/Blog5"));
const Blog6 = lazy(() => import("./components/Blog/Blog6"));
const Blog7 = lazy(() => import("./components/Blog/Blog7"));
const Blog8 = lazy(() => import("./components/Blog/Blog8"));
const Blog9 = lazy(() => import("./components/Blog/Blog9"));

// Service pages - lazy loaded
const ISO9001QMS = lazy(() => import("./pages/services/ISO9001QMS"));
const ISO14001EMS = lazy(() => import("./pages/services/ISO14001EMS"));
const OHSAS18001 = lazy(() => import("./pages/services/OHSAS18001"));
const ISO22000 = lazy(() => import("./pages/services/ISO22000"));
const ISO27001 = lazy(() => import("./pages/services/ISO27001"));
const BSEN16001 = lazy(() => import("./pages/services/BSEN16001"));
const ISO14001Consultants = lazy(
  () => import("./pages/services/ISO14001Consultants"),
);
const OHSAS18001Consultants = lazy(
  () => import("./pages/services/OHSAS18001Consultants"),
);
const HACCPConsultants = lazy(
  () => import("./pages/services/HACCPConsultants"),
);
const ISO22000Consultants = lazy(
  () => import("./pages/services/ISO22000Consultants"),
);
const ISO14001Certificate = lazy(
  () => import("./pages/services/ISO14001Certificate"),
);
const OHSAS18001Certificate = lazy(
  () => import("./pages/services/OHSAS18001Certificate"),
);
const HACCPISO22000Certificate = lazy(
  () => import("./pages/services/HACCPISO22000Certificate"),
);
const ISOCertificateUSA = lazy(
  () => import("./pages/services/ISOCertificateUSA"),
);
const ISOConsultantsBangalore = lazy(
  () => import("./pages/services/ISOConsultantsBangalore"),
);
const ISOConsultantsChennai = lazy(
  () => import("./pages/services/ISOConsultantsChennai"),
);
const ISOConsultantsHyderabad = lazy(
  () => import("./pages/services/ISOConsultantsHyderabad"),
);
const ISOConsultantsNewYork = lazy(
  () => import("./pages/services/ISOConsultantsNewYork"),
);
const IntegratedISOSystem = lazy(
  () => import("./pages/services/IntegratedISOSystem"),
);
const ISO9001ConsultantsDubai = lazy(
  () => import("./pages/services/ISO9001ConsultantsDubai"),
);
const ISO9001ConsultantsIndia = lazy(
  () => import("./pages/services/ISO9001ConsultantsIndia"),
);
const ISO9001ConsultantsSaudiArabia = lazy(
  () => import("./pages/services/ISO9001ConsultantsSaudiArabia"),
);
const ISOConsultantsUSA = lazy(
  () => import("./pages/services/ISOConsultantsUSA"),
);
const ISOConsultantsAfrica = lazy(
  () => import("./pages/services/ISOConsultantsAfrica"),
);
const OnlineISOTraining = lazy(
  () => import("./pages/services/OnlineISOTraining"),
);
const ISOCertificateKuwait = lazy(
  () => import("./pages/services/ISOCertificateKuwait"),
);
const ISOCertificateAfrica = lazy(
  () => import("./pages/services/ISOCertificateAfrica"),
);
const ISO9001ConsultantsKuwait = lazy(
  () => import("./pages/services/ISO9001ConsultantsKuwait"),
);
const ManufacturingDetroit = lazy(
  () => import("./pages/services/ManufacturingDetroit"),
);
const ManufacturingCleveland = lazy(
  () => import("./pages/services/ManufacturingCleveland"),
);
const ManufacturingHouston = lazy(
  () => import("./pages/services/ManufacturingHouston"),
);
const ManufacturingLosAngeles = lazy(
  () => import("./pages/services/ManufacturingLosAngeles"),
);
const ITSiliconValley = lazy(() => import("./pages/services/ITSiliconValley"));
const HealthcareBoston = lazy(
  () => import("./pages/services/HealthcareBoston"),
);
const FoodChicago = lazy(() => import("./pages/services/FoodChicago"));
const ConstructionHouston = lazy(
  () => import("./pages/services/ConstructionHouston"),
);
const ProfessionalNYC = lazy(() => import("./pages/services/ProfessionalNYC"));
const ISOTrainingCourses = lazy(
  () => import("./pages/services/ISOTrainingCourses"),
);

const BLOG_PATHS = {
  blog1: "/dont-get-tricked-by-fake-iso-certificates",
  blog2: "/what-is-the-actual-process-of-getting-an-iso-certification",
  blog3: "/how-to-select-suitable-iso-standard-for-your-organization",
  blog4: "/understanding-iso-certification-meaning-and-cost",
  blog5: "/iso-27001-2022-annex-a-control-updates",
  blog6: "/iso-22301-for-climate-ready-business-continuity-in-2026",
  blog7: "/iso-50001-energy-management-the-operational-backbone-of-net-zero",
  blog8: "/iso-27701-gdpr-building-accountable-privacy-management-in-2026",
  blog9: "/iso-37001-anti-bribery-programs-for-global-supply-chains",
};

function ScrollToTopButton() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 300);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  if (!visible) return null;

  return (
    <>
      <style>{`
        .scroll-to-top-btn {
          display: none;
        }
        @media (max-width: 768px) {
          .scroll-to-top-btn {
            display: flex;
            align-items: center;
            justify-content: center;
            position: fixed;
            bottom: 24px;
            right: 20px;
            width: 44px;
            height: 44px;
            border-radius: 50%;
            background: #0d6efd;
            color: #fff;
            border: none;
            box-shadow: 0 4px 12px rgba(0,0,0,0.25);
            z-index: 9999;
            cursor: pointer;
            font-size: 20px;
            transition: background 0.2s;
          }
          .scroll-to-top-btn:active {
            background: #0a58ca;
          }
        }
      `}</style>
      <button
        className="scroll-to-top-btn"
        onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
        aria-label="Back to top"
      >
        <i className="bi bi-chevron-up" />
      </button>
    </>
  );
}

export default function FrontEndApp() {
  const location = useLocation();

  useEffect(() => {
    if (typeof window.gtag !== "function") return;

    const pagePath = location.pathname + location.search + location.hash;
    const tagIds = ["AW-17354260913", "AW-18050931716"];

    tagIds.forEach((tagId) => {
      window.gtag("config", tagId, { page_path: pagePath });
    });
  }, [location.pathname, location.search, location.hash]);

  const globalFooterPaths = new Set([
    "/About-us",
    "/ShowCase",
    "/Services",
    "/BlogLanding",
    "/Contact",
    BLOG_PATHS.blog1,
    BLOG_PATHS.blog2,
    BLOG_PATHS.blog3,
    BLOG_PATHS.blog4,
    BLOG_PATHS.blog5,
    BLOG_PATHS.blog6,
    BLOG_PATHS.blog7,
    BLOG_PATHS.blog8,
    BLOG_PATHS.blog9,
    "/Blog1",
    "/Blog2",
    "/Blog3",
    "/Blog4",
    "/Blog5",
    "/Blog6",
    "/Blog7",
    "/Blog8",
    "/Blog9",
  ]);
  const shouldShowGlobalFooter = globalFooterPaths.has(location.pathname);

  return (
    <>
      <div className="fixed-top">
        <SocialBanner />
        <NavBarComponent />
      </div>

      <Suspense fallback={<LoadingSpinner />}>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/About-us" element={<AboutPage />} />
          <Route path="/ShowCase" element={<ShowCase />} />
          <Route path="/Services" element={<Services />} />
          <Route path="/BlogLanding" element={<BlogPage />} />
          <Route path="/Contact" element={<ContactPage />} />
          <Route path={BLOG_PATHS.blog1} element={<Blog1 />} />
          <Route path={BLOG_PATHS.blog2} element={<Blog2 />} />
          <Route path={BLOG_PATHS.blog3} element={<Blog3 />} />
          <Route path={BLOG_PATHS.blog4} element={<Blog4 />} />
          <Route path={BLOG_PATHS.blog5} element={<Blog5 />} />
          <Route path={BLOG_PATHS.blog6} element={<Blog6 />} />
          <Route path={BLOG_PATHS.blog7} element={<Blog7 />} />
          <Route path={BLOG_PATHS.blog8} element={<Blog8 />} />
          <Route path={BLOG_PATHS.blog9} element={<Blog9 />} />
          <Route
            path="/Blog1"
            element={<Navigate to={BLOG_PATHS.blog1} replace />}
          />
          <Route
            path="/Blog2"
            element={<Navigate to={BLOG_PATHS.blog2} replace />}
          />
          <Route
            path="/Blog3"
            element={<Navigate to={BLOG_PATHS.blog3} replace />}
          />
          <Route
            path="/Blog4"
            element={<Navigate to={BLOG_PATHS.blog4} replace />}
          />
          <Route
            path="/Blog5"
            element={<Navigate to={BLOG_PATHS.blog5} replace />}
          />
          <Route
            path="/Blog6"
            element={<Navigate to={BLOG_PATHS.blog6} replace />}
          />
          <Route
            path="/Blog7"
            element={<Navigate to={BLOG_PATHS.blog7} replace />}
          />
          <Route
            path="/Blog8"
            element={<Navigate to={BLOG_PATHS.blog8} replace />}
          />
          <Route
            path="/Blog9"
            element={<Navigate to={BLOG_PATHS.blog9} replace />}
          />
          {/* SEO Service Pages */}
          <Route path="/iso-9001-qms" element={<ISO9001QMS />} />
          <Route path="/iso-14001-ems" element={<ISO14001EMS />} />
          <Route path="/ohsas-18001" element={<OHSAS18001 />} />
          <Route path="/iso-22000" element={<ISO22000 />} />
          <Route path="/iso-27001" element={<ISO27001 />} />
          <Route path="/bs-en-16001" element={<BSEN16001 />} />
          <Route
            path="/iso-14001-consultants"
            element={<ISO14001Consultants />}
          />
          <Route
            path="/ohsas-18001-consultants"
            element={<OHSAS18001Consultants />}
          />
          <Route path="/haccp-consultants" element={<HACCPConsultants />} />
          <Route
            path="/iso-22000-consultants"
            element={<ISO22000Consultants />}
          />
          <Route
            path="/iso-14001-certificate"
            element={<ISO14001Certificate />}
          />
          <Route
            path="/ohsas-18001-certificate"
            element={<OHSAS18001Certificate />}
          />
          <Route
            path="/haccp-iso-22000-certificate"
            element={<HACCPISO22000Certificate />}
          />
          <Route path="/iso-certificate-usa" element={<ISOCertificateUSA />} />
          <Route
            path="/iso-consultants-bangalore"
            element={<ISOConsultantsBangalore />}
          />
          <Route
            path="/iso-consultants-chennai"
            element={<ISOConsultantsChennai />}
          />
          <Route
            path="/iso-consultants-hyderabad"
            element={<ISOConsultantsHyderabad />}
          />
          <Route
            path="/iso-consultants-new-york"
            element={<ISOConsultantsNewYork />}
          />
          <Route
            path="/integrated-iso-system"
            element={<IntegratedISOSystem />}
          />
          <Route
            path="/iso9001consultantsdubai"
            element={<ISO9001ConsultantsDubai />}
          />
          <Route
            path="/iso9001consultantsindia"
            element={<ISO9001ConsultantsIndia />}
          />
          <Route
            path="/iso9001consultantssaudiarabia"
            element={<ISO9001ConsultantsSaudiArabia />}
          />
          <Route path="/isoconsultantsusa" element={<ISOConsultantsUSA />} />
          <Route
            path="/isoconsultantsafrica"
            element={<ISOConsultantsAfrica />}
          />
          <Route path="/online-iso-training" element={<OnlineISOTraining />} />
          <Route
            path="/iso9001consultantskuwait"
            element={<ISO9001ConsultantsKuwait />}
          />
          <Route
            path="/isocertificatekuwait"
            element={<ISOCertificateKuwait />}
          />
          <Route
            path="/isocertificateafrica"
            element={<ISOCertificateAfrica />}
          />
          <Route
            path="/isoconsultantsbangalore"
            element={<ISOConsultantsBangalore />}
          />
          <Route
            path="/isoconsultantschennai"
            element={<ISOConsultantsChennai />}
          />
          <Route
            path="/isoconsultantshyderabad"
            element={<ISOConsultantsHyderabad />}
          />
          <Route
            path="/manufacturing-detroit"
            element={<ManufacturingDetroit />}
          />
          <Route
            path="/manufacturing-cleveland"
            element={<ManufacturingCleveland />}
          />
          <Route
            path="/manufacturing-houston"
            element={<ManufacturingHouston />}
          />
          <Route
            path="/manufacturing-los-angeles"
            element={<ManufacturingLosAngeles />}
          />
          <Route path="/it-silicon-valley" element={<ITSiliconValley />} />
          <Route path="/healthcare-boston" element={<HealthcareBoston />} />
          <Route path="/food-chicago" element={<FoodChicago />} />
          <Route
            path="/construction-houston"
            element={<ConstructionHouston />}
          />
          <Route path="/professional-nyc" element={<ProfessionalNYC />} />
          <Route
            path="/iso-9001-training-courses"
            element={<ISOTrainingCourses />}
          />
        </Routes>
      </Suspense>

      {shouldShowGlobalFooter && <ContactForm />}
      <ScrollToTopButton />
    </>
  );
}
